/**
 * DeleteMenu — Blender-style delete option popup.
 *
 * Shown when pressing X/Delete in edit mode. Mirrors Blender's full
 * delete menu: Vertices, Edges, Faces, Only Edges & Faces, Only Faces,
 * then dissolve operations (Dissolve Vertices/Edges/Faces).
 * Navigable with arrow keys + Enter.
 */

import { useCallback, useMemo } from "react";
import { useStore } from "../app/store.ts";
import type { CommandMenuOption } from "./FloatingCommandMenu.tsx";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";

export function DeleteMenu() {
  const menu = useOperatorMenu("delete");
  const deleteSelectedGeometry = useStore((s) => s.deleteSelectedGeometry);
  const deleteSelectedEdges = useStore((s) => s.deleteSelectedEdges);
  const deleteSelectedFaces = useStore((s) => s.deleteSelectedFaces);
  const deleteOnlyEdgesAndFaces = useStore((s) => s.deleteOnlyEdgesAndFaces);
  const deleteOnlyFaces = useStore((s) => s.deleteOnlyFaces);
  const dissolveSelectedVertices = useStore((s) => s.dissolveSelectedVertices);
  const dissolveSelectedEdges = useStore((s) => s.dissolveSelectedEdges);
  const dissolveSelectedFaces = useStore((s) => s.dissolveSelectedFaces);

  const options: CommandMenuOption[] = useMemo(
    () => [
      { id: "vertices", label: "Vertices", shortcutKey: "v" },
      { id: "edges", label: "Edges", shortcutKey: "e" },
      { id: "faces", label: "Faces", shortcutKey: "f" },
      { id: "only_edges_faces", label: "Only Edges & Faces", shortcutKey: "o" },
      { id: "only_faces", label: "Only Faces" },
      { id: "sep1", label: "", separator: true },
      { id: "dissolve_vertices", label: "Dissolve Vertices", shortcutKey: "d" },
      { id: "dissolve_edges", label: "Dissolve Edges" },
      { id: "dissolve_faces", label: "Dissolve Faces" },
    ],
    [],
  );

  // Undo is captured inside each delete/dissolve action (runEditOp) — the
  // menu only dispatches; it should not push its own snapshot.
  const handleSelect = useCallback(
    (id: string) => {
      switch (id) {
        case "vertices":
          deleteSelectedGeometry();
          break;
        case "edges":
          deleteSelectedEdges();
          break;
        case "faces":
          deleteSelectedFaces();
          break;
        case "only_edges_faces":
          deleteOnlyEdgesAndFaces();
          break;
        case "only_faces":
          deleteOnlyFaces();
          break;
        case "dissolve_vertices":
          dissolveSelectedVertices();
          break;
        case "dissolve_edges":
          dissolveSelectedEdges();
          break;
        case "dissolve_faces":
          dissolveSelectedFaces();
          break;
      }
      menu.close();
    },
    [
      menu,
      deleteSelectedGeometry,
      deleteSelectedEdges,
      deleteSelectedFaces,
      deleteOnlyEdgesAndFaces,
      deleteOnlyFaces,
      dissolveSelectedVertices,
      dissolveSelectedEdges,
      dissolveSelectedFaces,
    ],
  );

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={menu.close}
      title="Delete"
      anchorPoint={menu.anchorPoint}
      options={options}
      onSelect={handleSelect}
      panelKey="delete"
    />
  );
}
